import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAPBOX_TOKEN = 'pk.eyJ1IjoiZW1lcmdlbnRhaSIsImEiOiJjbTJ4eGRqdzEwMWhlMmtzZDZweW14N3Y4In0.cw1XwV0EwJlcS2j_3a6ydw';

export const CRM = ({ user }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stages, setStages] = useState([]);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [showNewLead, setShowNewLead] = useState(false);
  const [showEditLead, setShowEditLead] = useState(false);
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [extractedText, setExtractedText] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [sources, setSources] = useState([]);
  const [sourceInput, setSourceInput] = useState('');
  const [showSourceSuggestions, setShowSourceSuggestions] = useState(false);
  
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    country: '',
    value: '',
    source: '',
    website: '',
    stage_id: '',
    notes: ''
  });

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      await Promise.all([
        fetchStages(),
        fetchLeads(),
        fetchStats(),
        fetchSources()
      ]);
    } catch (error) {
      console.error('Error fetching CRM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crm/stages`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setStages(response.data);
      
      // Initialize stages if none exist
      if (response.data.length === 0) {
        await axios.post(`${BACKEND_URL}/api/crm/init-stages`, {}, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        fetchStages();
      }
    } catch (error) {
      console.error('Error fetching stages:', error);
    }
  };

  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crm/leads`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crm/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSources = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crm/sources`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSources(response.data);
    } catch (error) {
      console.error('Error fetching sources:', error);
    }
  };

  const handleAIExtract = async () => {
    if (!extractedText.trim()) {
      alert('Please paste some text to extract lead information');
      return;
    }

    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append('text', extractedText);

      const response = await axios.post(`${BACKEND_URL}/api/crm/extract-lead`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Fill the form with extracted data
      setNewLead({
        name: response.data.name || newLead.name,
        email: response.data.email || newLead.email,
        phone: response.data.phone || newLead.phone,
        company: response.data.company || newLead.company,
        country: response.data.country || newLead.country,
        value: response.data.value || newLead.value,
        source: response.data.source || newLead.source,
        website: response.data.website || newLead.website,
        stage_id: newLead.stage_id || (stages.length > 0 ? stages[0].id : ''),
        notes: response.data.notes || newLead.notes
      });

      setExtractedText('');
      
    } catch (error) {
      alert('Error extracting lead: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExtracting(false);
    }
  };

  const filterSources = (input) => {
    if (!input) return [];
    return sources.filter(source => 
      source.toLowerCase().includes(input.toLowerCase())
    );
  };

  const handleCreateLead = async () => {
    try {
      const formData = new FormData();
      Object.keys(newLead).forEach(key => {
        if (newLead[key]) {
          formData.append(key, newLead[key]);
        }
      });

      await axios.post(`${BACKEND_URL}/api/crm/leads`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setNewLead({
        name: '',
        email: '',
        phone: '',
        company: '',
        country: '',
        value: '',
        source: '',
        website: '',
        stage_id: '',
        notes: ''
      });
      setShowNewLead(false);
      fetchLeads();
      fetchStats();
      fetchSources();
    } catch (error) {
      alert('Error creating lead: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateLead = async (leadId, updates) => {
    try {
      const formData = new FormData();
      Object.keys(updates).forEach(key => {
        if (updates[key] !== null && updates[key] !== undefined) {
          formData.append(key, updates[key]);
        }
      });

      await axios.put(`${BACKEND_URL}/api/crm/leads/${leadId}`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      fetchLeads();
      fetchStats();
      fetchSources();
    } catch (error) {
      console.error('Error updating lead:', error);
      alert('Error updating lead: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSaveEditedLead = async () => {
    if (!editingLead.name || !editingLead.stage_id) {
      alert('Name and Stage are required');
      return;
    }

    await handleUpdateLead(editingLead.id, {
      name: editingLead.name,
      email: editingLead.email,
      phone: editingLead.phone,
      company: editingLead.company,
      country: editingLead.country,
      value: editingLead.value,
      source: editingLead.source,
      website: editingLead.website,
      stage_id: editingLead.stage_id,
      notes: editingLead.notes
    });

    setShowEditLead(false);
    setEditingLead(null);
  };

  const openEditLead = (lead) => {
    setEditingLead({ ...lead });
    setShowEditLead(true);
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/crm/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      fetchLeads();
      fetchStats();
      setShowLeadDetail(false);
    } catch (error) {
      alert('Error deleting lead: ' + error.message);
    }
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { draggableId, destination } = result;
    const leadId = draggableId;
    const newStageId = destination.droppableId;

    // Update lead stage
    handleUpdateLead(leadId, { stage_id: newStageId });
  };

  const openLeadDetail = async (lead) => {
    setSelectedLead(lead);
    setShowLeadDetail(true);
    
    // Fetch activities
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crm/leads/${lead.id}/activities`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setActivities(response.data);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedLead) return;

    try {
      const formData = new FormData();
      formData.append('comment', newComment);

      await axios.post(`${BACKEND_URL}/api/crm/leads/${selectedLead.id}/comment`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setNewComment('');
      // Refresh activities
      const response = await axios.get(`${BACKEND_URL}/api/crm/leads/${selectedLead.id}/activities`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setActivities(response.data);
    } catch (error) {
      alert('Error adding comment: ' + error.message);
    }
  };

  const getLeadsByStage = (stageId) => {
    return leads.filter(lead => lead.stage_id === stageId);
  };

  const formatCurrency = (value) => {
    if (!value) return '€0';
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(value);
  };

  const getUserInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(word => word[0]).slice(0, 2).join('').toUpperCase();
  };

  const getCountryFlag = (countryName) => {
    if (!countryName) return null;
    
    const countryToCode = {
      'iceland': 'is', 'norway': 'no', 'denmark': 'dk', 'sweden': 'se', 'finland': 'fi',
      'germany': 'de', 'france': 'fr', 'spain': 'es', 'italy': 'it', 'uk': 'gb',
      'united kingdom': 'gb', 'usa': 'us', 'united states': 'us', 'canada': 'ca',
      'netherlands': 'nl', 'belgium': 'be', 'switzerland': 'ch', 'austria': 'at',
      'poland': 'pl', 'portugal': 'pt', 'ireland': 'ie', 'greece': 'gr',
      'czech republic': 'cz', 'hungary': 'hu', 'romania': 'ro', 'slovakia': 'sk',
      'bulgaria': 'bg', 'croatia': 'hr', 'estonia': 'ee', 'latvia': 'lv',
      'lithuania': 'lt', 'slovenia': 'si', 'luxembourg': 'lu', 'malta': 'mt',
      'cyprus': 'cy', 'australia': 'au', 'new zealand': 'nz', 'japan': 'jp',
      'china': 'cn', 'india': 'in', 'brazil': 'br', 'mexico': 'mx',
      'argentina': 'ar', 'chile': 'cl', 'south africa': 'za', 'egypt': 'eg',
      'turkey': 'tr', 'russia': 'ru', 'ukraine': 'ua', 'south korea': 'kr',
      'singapore': 'sg', 'malaysia': 'my', 'thailand': 'th', 'vietnam': 'vn',
      'philippines': 'ph', 'indonesia': 'id', 'saudi arabia': 'sa', 'uae': 'ae',
      'united arab emirates': 'ae', 'israel': 'il', 'cabo verde': 'cv', 'drc': 'cd',
      'dr congo': 'cd', 'democratic republic of congo': 'cd'
    };
    
    return countryToCode[countryName.toLowerCase()] || countryName.substring(0, 2).toLowerCase();
  };

  if (loading) {
    return <div className="text-center py-8">Loading CRM...</div>;
  }

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">📊 Dashboard</TabsTrigger>
          <TabsTrigger value="pipeline">🎯 Pipeline</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Total Leads</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.total_leads || 0}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Pipeline Value</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{formatCurrency(stats?.pipeline_value)}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Conversion Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.conversion_rate?.toFixed(1) || 0}%</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Avg Deal Value</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{formatCurrency(stats?.avg_deal_value)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Won/Lost Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Won vs Lost</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-green-600 font-semibold">✓ Won</span>
                    <span className="text-2xl font-bold">{stats?.won_count || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-red-600 font-semibold">✗ Lost</span>
                    <span className="text-2xl font-bold">{stats?.lost_count || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Leads by Stage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(stats?.leads_by_stage || {}).map(([stage, count]) => (
                    <div key={stage} className="flex justify-between items-center">
                      <span className="text-sm">{stage}</span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Map Placeholder */}
          <Card>
            <CardHeader>
              <CardTitle>Leads by Location</CardTitle>
              <CardDescription>Geographic distribution of your leads</CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.leads_by_country && Object.keys(stats.leads_by_country).length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(stats.leads_by_country).map(([country, count]) => (
                    <div key={country} className="border rounded p-3">
                      <div className="text-sm text-gray-600">{country}</div>
                      <div className="text-2xl font-bold">{count}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No location data yet. Add countries to your leads!</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline">
          <div className="mb-4">
            <Button onClick={() => setShowNewLead(true)}>
              + New Lead
            </Button>
          </div>

          {/* Kanban Board */}
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="flex gap-4 overflow-x-auto pb-4">
              {stages.map(stage => (
                <div key={stage.id} className="flex-shrink-0 w-80">
                  <div className="bg-gray-100 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold flex items-center gap-2">
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: stage.color }}
                        ></span>
                        {stage.name}
                      </h3>
                      <span className="text-sm text-gray-600">
                        {getLeadsByStage(stage.id).length}
                      </span>
                    </div>

                    <Droppable droppableId={stage.id}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`space-y-2 min-h-[200px] ${
                            snapshot.isDraggingOver ? 'bg-blue-50' : ''
                          }`}
                        >
                          {getLeadsByStage(stage.id).map((lead, index) => (
                            <Draggable
                              key={lead.id}
                              draggableId={lead.id}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`bg-white rounded-lg p-3 shadow-sm border cursor-pointer hover:shadow-md transition-shadow ${
                                    snapshot.isDragging ? 'shadow-lg' : ''
                                  }`}
                                  onClick={() => openLeadDetail(lead)}
                                >
                                  <div className="flex items-start justify-between mb-2">
                                    <div className="font-medium text-sm">{lead.name}</div>
                                    {/* Owner Avatar */}
                                    {lead.owner_photo_url ? (
                                      <img
                                        src={lead.owner_photo_url}
                                        alt={lead.owner_name}
                                        className="w-8 h-8 rounded-full object-cover"
                                        title={lead.owner_name}
                                      />
                                    ) : (
                                      <div
                                        className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold"
                                        title={lead.owner_name}
                                      >
                                        {lead.owner_initials || getUserInitials(lead.owner_name)}
                                      </div>
                                    )}
                                  </div>
                                  {lead.company && (
                                    <div className="text-xs text-gray-600 mb-1">{lead.company}</div>
                                  )}
                                  {lead.value && (
                                    <div className="text-sm font-semibold text-green-600">
                                      {formatCurrency(lead.value)}
                                    </div>
                                  )}
                                  {lead.country && (
                                    <div className="text-xs mt-2 flex items-center gap-2">
                                      <span className={`fi fi-${getCountryFlag(lead.country)}`} style={{ fontSize: '16px' }}></span>
                                      <span className="text-gray-600">{lead.country}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                </div>
              ))}
            </div>
          </DragDropContext>
        </TabsContent>
      </Tabs>

      {/* New Lead Dialog */}
      <Dialog open={showNewLead} onOpenChange={setShowNewLead}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Lead</DialogTitle>
          </DialogHeader>
          
          {/* AI Quick Add Section */}
          <div className="border-b pb-4 mb-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="text-2xl">🤖</div>
                <div className="flex-1">
                  <Label className="text-sm font-semibold text-blue-900">Quick Add with AI</Label>
                  <p className="text-xs text-blue-700 mt-1 mb-3">
                    Paste lead information (email signatures, LinkedIn profiles, business cards, etc.) and AI will extract the fields automatically.
                  </p>
                  <textarea
                    className="w-full border rounded px-3 py-2 text-sm min-h-[100px]"
                    value={extractedText}
                    onChange={(e) => setExtractedText(e.target.value)}
                    placeholder="Example: John Smith, CEO at Tech Corp, john@techcorp.com, +1-555-0123, https://techcorp.com, Looking for airport solutions, budget €50,000"
                  />
                  <Button 
                    size="sm" 
                    onClick={handleAIExtract} 
                    disabled={!extractedText.trim() || extracting}
                    className="mt-2"
                  >
                    {extracting ? '🔄 Extracting...' : '✨ Extract & Fill Fields'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={newLead.name}
                onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                placeholder="Lead name"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newLead.email}
                onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={newLead.phone}
                onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                placeholder="+1234567890"
              />
            </div>
            <div>
              <Label>Company</Label>
              <Input
                value={newLead.company}
                onChange={(e) => setNewLead({ ...newLead, company: e.target.value })}
                placeholder="Company name"
              />
            </div>
            <div>
              <Label>Country</Label>
              <Input
                value={newLead.country}
                onChange={(e) => setNewLead({ ...newLead, country: e.target.value })}
                placeholder="USA"
              />
            </div>
            <div>
              <Label>Value (€)</Label>
              <Input
                type="number"
                value={newLead.value}
                onChange={(e) => setNewLead({ ...newLead, value: e.target.value })}
                placeholder="10000"
              />
            </div>
            <div className="relative">
              <Label>Source</Label>
              <Input
                value={newLead.source}
                onChange={(e) => {
                  setNewLead({ ...newLead, source: e.target.value });
                  setShowSourceSuggestions(e.target.value.length > 0);
                }}
                onFocus={() => setShowSourceSuggestions(newLead.source.length > 0)}
                onBlur={() => setTimeout(() => setShowSourceSuggestions(false), 200)}
                placeholder="LinkedIn, Website, Referral..."
              />
              {showSourceSuggestions && filterSources(newLead.source).length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto">
                  {filterSources(newLead.source).map(source => (
                    <div
                      key={source}
                      className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setNewLead({ ...newLead, source });
                        setShowSourceSuggestions(false);
                      }}
                    >
                      {source}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Website</Label>
              <Input
                value={newLead.website}
                onChange={(e) => setNewLead({ ...newLead, website: e.target.value })}
                placeholder="https://example.com"
              />
            </div>
            <div>
              <Label>Stage *</Label>
              <select
                className="w-full border rounded px-3 py-2"
                value={newLead.stage_id}
                onChange={(e) => setNewLead({ ...newLead, stage_id: e.target.value })}
              >
                <option value="">Select stage</option>
                {stages.map(stage => (
                  <option key={stage.id} value={stage.id}>{stage.name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <textarea
                className="w-full border rounded px-3 py-2"
                rows="3"
                value={newLead.notes}
                onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                placeholder="Additional notes..."
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowNewLead(false)}>Cancel</Button>
            <Button onClick={handleCreateLead} disabled={!newLead.name || !newLead.stage_id}>
              Create Lead
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={showLeadDetail} onOpenChange={setShowLeadDetail}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedLead?.country && (
                <span className={`fi fi-${getCountryFlag(selectedLead.country)}`} style={{ fontSize: '24px' }}></span>
              )}
              {selectedLead?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedLead?.company && `${selectedLead.company}`}
              {selectedLead?.country && ` • ${selectedLead.country}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Lead Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-gray-600">Email</Label>
                <div>{selectedLead?.email || 'N/A'}</div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Phone</Label>
                <div>{selectedLead?.phone || 'N/A'}</div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Value</Label>
                <div className="text-green-600 font-semibold">
                  {formatCurrency(selectedLead?.value)}
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Source</Label>
                <div>{selectedLead?.source || 'N/A'}</div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Website</Label>
                <div>
                  {selectedLead?.website ? (
                    <a href={selectedLead.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {selectedLead.website}
                    </a>
                  ) : 'N/A'}
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Owner</Label>
                <div className="flex items-center gap-2">
                  {selectedLead?.owner_photo_url ? (
                    <img
                      src={selectedLead.owner_photo_url}
                      alt={selectedLead.owner_name}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs">
                      {selectedLead?.owner_initials}
                    </div>
                  )}
                  {selectedLead?.owner_name}
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Created</Label>
                <div>{selectedLead?.created_at && new Date(selectedLead.created_at).toLocaleDateString()}</div>
              </div>
            </div>

            {/* Notes */}
            {selectedLead?.notes && (
              <div>
                <Label className="text-xs text-gray-600">Notes</Label>
                <div className="mt-1 p-3 bg-gray-50 rounded">{selectedLead.notes}</div>
              </div>
            )}

            {/* Activities */}
            <div>
              <Label className="text-xs text-gray-600 mb-2 block">Comments & Activity Log</Label>
              <div className="max-h-64 overflow-y-auto space-y-3 mb-3">
                {activities.length === 0 ? (
                  <p className="text-sm text-gray-500">No comments or activities yet</p>
                ) : (
                  activities.map(activity => (
                    <div key={activity.id} className="flex gap-3 border-l-2 border-blue-500 pl-3 py-2">
                      {/* User Avatar */}
                      {activity.user_photo_url ? (
                        <img
                          src={activity.user_photo_url}
                          alt={activity.user_name}
                          className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
                          {getUserInitials(activity.user_name)}
                        </div>
                      )}
                      
                      {/* Comment Content */}
                      <div className="flex-1">
                        <div className="font-medium text-sm">{activity.description}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {activity.user_name} • {new Date(activity.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              {/* Add Comment */}
              <div className="border-t pt-3">
                <Label className="text-xs text-gray-600 mb-2 block">Add Comment/Action</Label>
                <div className="flex gap-2">
                  <textarea
                    className="flex-1 border rounded px-3 py-2 text-sm"
                    rows="2"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment or log an action..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        handleAddComment();
                      }
                    }}
                  />
                  <Button 
                    size="sm" 
                    onClick={handleAddComment} 
                    disabled={!newComment.trim()}
                    className="self-end"
                  >
                    Post
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Tip: Press Ctrl+Enter to post</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between gap-2 pt-4 border-t">
              <Button 
                onClick={() => {
                  openEditLead(selectedLead);
                  setShowLeadDetail(false);
                }}
              >
                ✏️ Edit Lead
              </Button>
              <div className="flex gap-2">
                <Button variant="destructive" onClick={() => handleDeleteLead(selectedLead?.id)}>
                  Delete Lead
                </Button>
                <Button variant="outline" onClick={() => setShowLeadDetail(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={showEditLead} onOpenChange={setShowEditLead}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
          </DialogHeader>
          {editingLead && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={editingLead.name}
                  onChange={(e) => setEditingLead({ ...editingLead, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editingLead.email || ''}
                  onChange={(e) => setEditingLead({ ...editingLead, email: e.target.value })}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={editingLead.phone || ''}
                  onChange={(e) => setEditingLead({ ...editingLead, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Company</Label>
                <Input
                  value={editingLead.company || ''}
                  onChange={(e) => setEditingLead({ ...editingLead, company: e.target.value })}
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={editingLead.country || ''}
                  onChange={(e) => setEditingLead({ ...editingLead, country: e.target.value })}
                />
              </div>
              <div>
                <Label>Value (€)</Label>
                <Input
                  type="number"
                  value={editingLead.value || ''}
                  onChange={(e) => setEditingLead({ ...editingLead, value: e.target.value })}
                />
              </div>
              <div>
                <Label>Source</Label>
                <Input
                  value={editingLead.source || ''}
                  onChange={(e) => setEditingLead({ ...editingLead, source: e.target.value })}
                />
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={editingLead.website || ''}
                  onChange={(e) => setEditingLead({ ...editingLead, website: e.target.value })}
                />
              </div>
              <div>
                <Label>Stage *</Label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={editingLead.stage_id}
                  onChange={(e) => setEditingLead({ ...editingLead, stage_id: e.target.value })}
                >
                  {stages.map(stage => (
                    <option key={stage.id} value={stage.id}>{stage.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <textarea
                  className="w-full border rounded px-3 py-2"
                  rows="3"
                  value={editingLead.notes || ''}
                  onChange={(e) => setEditingLead({ ...editingLead, notes: e.target.value })}
                />
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowEditLead(false)}>Cancel</Button>
            <Button onClick={handleSaveEditedLead}>
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
